import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../config/axios';
import ModalConfirm from '../../components/ModalConfirm';
import ModalResult from '../../components/ModalResult';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

const TeacherDetailsExtra = ({ teacherId, otherCompensations, onUpdate }) => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [resultModal, setResultModal] = useState({ open: false, message: '', severity: 'info' });

  const [formData, setFormData] = useState({
    amount: '',
    date: dayjs().format('YYYY-MM-DD'),
    note: ''
  });

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        amount: item.amount.toString(),
        date: dayjs(item.date).format('YYYY MM DD'),
        note: item.note || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        amount: '',
        date: dayjs().format('YYYY MM DD'),
        note: ''
      });
    }
    setError('');
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        amount: parseFloat(formData.amount),
        date: formData.date,
        note: formData.note || undefined
      };

      if (editingItem) {
        await apiClient.patch(`/api/teacher/${teacherId}/other-compensations/${editingItem._id}`, payload);
      } else {
        await apiClient.post(`/api/teacher/${teacherId}/other-compensations`, payload);
      }

      handleCloseModal();
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setConfirmDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await apiClient.delete(`/api/teacher/${teacherId}/other-compensations/${id}`);
      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      setResultModal({
        open: true,
        message: err.response?.data?.message || 'Errore nell\'eliminazione',
        severity: 'error'
      });
    }
  };

  const total = (otherCompensations || []).reduce((sum, item) => sum + item.amount, 0);

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Altri Rimborsi
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenModal()}
            >
              Nuovo rimborso
            </Button>
          )}
        </Box>

        {(!otherCompensations || otherCompensations.length === 0) ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nessun altro rimborso registrato
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Data</strong></TableCell>
                  <TableCell align="right"><strong>Importo</strong></TableCell>
                  <TableCell><strong>Nota</strong></TableCell>
                  {isAdmin && <TableCell align="right"><strong>Azioni</strong></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {otherCompensations.map((item) => (
                  <TableRow key={item._id}>
                    <TableCell>
                      {dayjs(item.date).format('DD MMM YY')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="medium">
                        €{item.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.note || '-'}
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(item)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(item._id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell><strong>Totale</strong></TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" fontWeight="bold">
                      €{total.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={isAdmin ? 2 : 1} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={(e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleCloseModal();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>
          {editingItem ? 'Modifica rimborso' : 'Nuovo rimborso'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Importo (€)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              InputProps={{
                inputProps: { min: 0, step: 0.01 }
              }}
              required
              fullWidth
            />
            <TextField
              label="Data"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              required
            />
            <TextField
              label="Nota"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={loading}>
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.amount || !formData.date}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <ModalConfirm
        open={confirmDeleteId !== null}
        title="Elimina rimborso"
        message="Sei sicuro di voler eliminare questo rimborso?"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteId(null)}
      />

      <ModalResult
        open={resultModal.open}
        onClose={() => setResultModal((prev) => ({ ...prev, open: false }))}
        message={resultModal.message}
        severity={resultModal.severity}
      />
    </>
  );
};

export default TeacherDetailsExtra;
